import * as React from "react";
import { Route, Switch } from "react-router-dom";
import classNames from "classnames";

//#region component imports
import ScrollToTop from "./components/common/ScrollToTop";
import FourOhFourPage from "./common/404Page";
import FourOhOnePage from "./common/401Page";
import PageFooter from "./components/common/PageFooter";
import ResetPasswordPage from "./common/ResetPasswordPage";

import LandingPage from "./common/LandingPage";

import PickListTypeListPage from "./admin/PickList/PickListTypeListPage";
import PickListItemListPage from "./admin/PickList/PickListItemListPage";

import PickListTypeDetails from "./admin/PickList/PickListTypeDetails";
import PickListItemDetails from "./admin/PickList/PickListItemDetails";

import OrganizationRoles from "./admin/Organization/Roles/OrganizationRoles";
import OrganizationRoleForm from "./admin/Organization/Roles/OrganizationRoleForm";

import OrganizationListPage from "./admin/Organization/OrganizationListPage";

import OrganizationLocations from "./admin/Organization/Location/OrganizationLocations";
import LocationDetailsForm from "./admin/Organization/Location/LocationDetails";

import OrganizationUsers from "./admin/Organization/Users/OrganizationUsers";
import OrganizationUsersTabs from "./admin/Organization/Tabs/OrganizationUsersTabs";
import OrganizationUsersDetails from "./admin/Organization/Users/OrganizationUsersDetails";

import OrganizationReportSettings from "./admin/Organization/ReportSetting/OrganizationReportSettings";

import OrganizationCustomFields from "./admin/Organization/CustomFields/OrganizationCustomFields";
import OrganizationCustomFieldsEdit from "./admin/Organization/CustomFields/OrganizationCustomFieldsEdit";

import OrganizationDatasets from "./admin/Organization/Datasets/OrganizationDatasets";
import OrganizationDatasetsTabs from "./admin/Organization/Datasets/OrganizationDatasetsTabs";
import OrganizationDatasetDetails from "./admin/Organization/Datasets/OrganizationDatasetDetails";
import OrganizationDatasetLocations from "./admin/Organization/Datasets/OrganizationDatasetLocations";
import OrganizationDatasetsUsers from "./admin/Organization/Datasets/OrganizationDatasetsUsers";
import OrganizationDatasetsCutpoints from "./admin/Organization/Datasets/OrganizationDatasetsCutpoints";
import OrganizationDatasetsReportSettings from "./admin/Organization/Datasets/OrganizationDatasetsReportSettings";

import OrganizationCourses from "./admin/Organization/Courses/OrganizationCourses";
import OrganizationCoursesEdit from "./admin/Organization/Courses/OrganizationCoursesEdit";

import DatasetListView from "./clients/DatasetListView";
import MultiOrgDatasetListView from "./clients/MultiOrgDatasetListView";
import ClientsListView from "./clients/ClientsListView";
import ClientDetails from "./clients/ClientDetails";
import ClientResults from "./clients/ClientResults";
import ClientTab from "./clients/ClientTab";

import FleetErrors from "./admin/Errors/FleetErrors";
import FleetErrorsDetails from "./admin/Errors/FleetErrorsDetails";

import DatasetAssessmentResults from "./clientsiteresults/DatasetAssessmentResults";
import ReportList from "./admin/report/ReportList";
import ReportsTabs from "./admin/report/ReportsTabs";
import ReportTemplateContainer from "./admin/report/ReportTemplateContainer";
import DatasetReportTemplateContainer from "./clientsiteresults/DatasetReportTemplateContainer";

import CoreGeneral from "./core/CoreGeneral";
import CoreSkillAppraisal from "./core/CoreSkillAppraisal";
import CoreErrorEntry from "./core/CoreErrorEntry";
import CoreTabs from "./core/CoreTabs";

import ProfileTabs from "./profile/ProfileTabs";
import UserSettings from "./profile/Settings";
import UserPassword from "./profile/Password";

import OrganizationDetails from "./admin/Organization/OrganizationDetails";

import OrganizationPage from "./admin/Organization/OrganizationPage";
//#endregion

import { Permissions, DatasetPermissions } from "./common/Authorization";
import requiresPermissions from "./context/requiresPermissions";
import requiresDatasetPermissions from "./context/requiresDatasetPermissions";
import LocationPage from "./admin/Organization/Location/LocationPage";
import VitalsSummary from "./vitals/VitalsSummaryClass";
import { apiHOC } from "./context/apiHOC";
import { userHOC } from "./context/userHOC";
import { UserManagementPage } from "./admin/Users/UserManagementPage";
import { UserListingPage } from "./admin/Users/UserListingPage";
import { VitalsMSummary } from "./vitals/VitalsMSummary";
import OrganizationDatasetsPII from "./admin/Organization/Datasets/OrganizationDatasetsPII";

import ClientCoursePage from "./training/ClientCoursePage";
import ClientTrainingPage from "./training/ClientTrainingPage";

import TrainingManagementPage from "./admin/Training/TrainingManagementPage";
import AddEditCoursesPage from "./admin/Training/AddEditCoursesPage";
import OrganizationTrainingSummary from "./admin/Organization/TrainingSummary/OrganizationTrainingSummary";
import OrganizationDatasetsBilling from "./admin/Organization/Datasets/OrganizationDatasetsBilling";

const DatasetAssessmentResultsWithUser = userHOC(
	apiHOC(DatasetAssessmentResults)
);
const ClientResultsWithUser = userHOC(apiHOC(ClientResults));
const ClientsListWithUser = userHOC(apiHOC(ClientsListView));
const OrganizationUsersWithUsersClient = apiHOC(OrganizationUsers);
const ReportListWithApi = apiHOC(ReportList);
const CoreErrorEntryWithApi = apiHOC(CoreErrorEntry);

class Routes extends React.Component<any, {}> {
	public render() {
		return (
			<ScrollToTop
				className={classNames("wrapper", {
					"logged-in": true, // this.props.auth.token.userId
				})}
				location={this.props.location}
			>
				<Switch>
					<Route path="/401" component={FourOhOnePage} />
					<Route
						path="/admin/users"
						render={(props) => (
							<UserManagementPage {...props}>
								<Route
									exact={true}
									path="/admin/users/"
									component={UserListingPage}
								/>
							</UserManagementPage>
						)}
					/>
					<Route
						exact={true}
						path="/admin/training"
						component={requiresPermissions(
							TrainingManagementPage,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/admin/training/:trainingCourseId"
						component={requiresPermissions(
							AddEditCoursesPage,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/admin/organizations/:organizationId/roles/:roleId"
						component={requiresPermissions(
							OrganizationRoleForm,
							Permissions.OrganizationRolesDetails
						)}
					/>
					<Route
						exact={true}
						path="/admin/organizations/:organizationId/locations/:locationId/courses/:courseId"
						component={requiresPermissions(
							OrganizationCoursesEdit,
							Permissions.OrganizationLocationDetails
						)}
					/>
					<Route
						path="/admin/organizations/:organizationId/locations/:locationId"
						render={(props) => (
							<LocationPage {...props}>
								{(setLocationNameInTab: any) => {
									const Component = requiresPermissions(
										LocationDetailsForm,
										Permissions.OrganizationLocationDetails
									);
									return (
										<React.Fragment>
											<Route
												exact={true}
												path="/admin/organizations/:organizationId/locations/:locationId"
												render={(p: any) => (
													<Component
														setLocationNameInTab={setLocationNameInTab}
														{...p}
													/>
												)}
											/>
											<Route
												exact={true}
												path="/admin/organizations/:organizationId/locations/:locationId/courses"
												component={requiresPermissions(
													OrganizationCourses,
													Permissions.OrganizationLocationDetails
												)}
											/>
										</React.Fragment>
									);
								}}
							</LocationPage>
						)}
					/>

					<Route
						exact={true}
						path="/admin/organizations/:organizationId/datasets/:datasetId/locations/:locationId"
						component={LocationDetailsForm}
					/>
					<Route
						exact={true}
						path="/admin/organizations/:organizationId/custom-fields/:customFieldId"
						component={requiresPermissions(
							OrganizationCustomFieldsEdit,
							Permissions.OrganizationCustomFieldDetails
						)}
					/>
					<Route
						path="/admin/organizations/:organizationId/users/:userId/"
						render={(props) => (
							<OrganizationUsersTabs {...props}>
								{(setUsernameInTab: any) => {
									const Component = requiresPermissions(
										OrganizationUsersDetails,
										Permissions.OrganizationUserDetails
									);
									return (
										<>
											<Route
												exact={true}
												path="/admin/organizations/:organizationId/users/:userId/details"
												component={(p: any) => (
													<Component
														setUsernameInTab={setUsernameInTab}
														{...p}
													/>
												)}
											/>
										</>
									);
								}}
							</OrganizationUsersTabs>
						)}
					/>

					<Route
						path="/admin/organizations/:organizationId/datasets/:datasetId/"
						render={(props) => (
							<OrganizationDatasetsTabs {...props}>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/details"
									component={requiresPermissions(
										OrganizationDatasetDetails,
										Permissions.OrganizationDatasetDetails
									)}
								/>
								{/* Add the permission checker */}
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/locations"
									component={OrganizationDatasetLocations}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/users"
									component={requiresPermissions(
										OrganizationDatasetsUsers,
										Permissions.OrganizationDatasetList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/cutpoints"
									component={requiresPermissions(
										OrganizationDatasetsCutpoints,
										Permissions.OrganizationDatasetCutpoints
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/report-settings"
									component={requiresPermissions(
										OrganizationDatasetsReportSettings,
										Permissions.OrganizationDatasetReportSetting
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/personal-fields"
									component={requiresPermissions(
										OrganizationDatasetsPII,
										Permissions.OrganizationDatasetDetails
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets/:datasetId/billing"
									component={requiresPermissions(
										OrganizationDatasetsBilling,
										Permissions.OrganizationDatasetBilling
									)}
								/>
							</OrganizationDatasetsTabs>
						)}
					/>

					<Route
						path="/admin/organizations/:organizationId"
						render={(props) => (
							<OrganizationPage {...props}>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/locations"
									component={requiresPermissions(
										OrganizationLocations,
										Permissions.OrganizationLocationList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/roles"
									component={requiresPermissions(
										OrganizationRoles,
										Permissions.OrganizationRolesList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/datasets"
									component={requiresPermissions(
										OrganizationDatasets,
										Permissions.OrganizationDatasetList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/users"
									component={requiresPermissions(
										OrganizationUsersWithUsersClient,
										Permissions.OrganizationUserList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId"
									component={requiresPermissions(
										OrganizationDetails,
										Permissions.OrganizationList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/report-settings"
									component={requiresPermissions(
										OrganizationReportSettings,
										Permissions.OrganizationReportSetting
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/custom-fields"
									component={requiresPermissions(
										OrganizationCustomFields,
										Permissions.OrganizationCustomFieldList
									)}
								/>
								<Route
									exact={true}
									path="/admin/organizations/:organizationId/training-summary"
									component={requiresPermissions(
										OrganizationTrainingSummary,
										Permissions.GlobalAdmins
									)}
								/>
							</OrganizationPage>
						)}
					/>

					<Route
						exact={true}
						path="/admin/organizations"
						component={requiresPermissions(
							OrganizationListPage,
							Permissions.OrganizationList
						)}
					/>

					<Route
						exact={true}
						path="/admin/fleet-errors"
						component={requiresPermissions(
							FleetErrors,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/admin/fleet-errors/:fleetErrorId"
						component={requiresPermissions(
							FleetErrorsDetails,
							Permissions.GlobalAdmins
						)}
					/>

					<Route
						exact={true}
						path="/admin/reports/:reportId"
						component={ReportTemplateContainer}
					/>
					<Route
						path="/admin/reports"
						render={(props) => (
							<ReportsTabs {...props}>
								<Route
									exact={true}
									path="/admin/reports"
									component={ReportListWithApi}
								/>
							</ReportsTabs>
						)}
					/>

					<Route
						exact={true}
						path="/admin/pick-lists"
						component={requiresPermissions(
							PickListTypeListPage,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/admin/pick-lists/:pickListTypeId"
						component={requiresPermissions(
							PickListTypeDetails,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/admin/pick-lists/:pickListTypeId/pick-list-items"
						component={requiresPermissions(
							PickListItemListPage,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/admin/pick-lists/:pickListTypeId/pick-list-items/:pickListItemId"
						component={requiresPermissions(
							PickListItemDetails,
							Permissions.GlobalAdmins
						)}
					/>

					<Route
						path="/profile"
						render={(props) => (
							<ProfileTabs {...props}>
								<Route
									exact={true}
									path="/profile/settings"
									component={UserSettings}
								/>
								<Route
									exact={true}
									path="/profile/password"
									component={UserPassword}
								/>
							</ProfileTabs>
						)}
					/>

					<Route
						exact={true}
						path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/sessionId/:sessionId/vitalsTestResults/:vitalsTestResultId"
						component={requiresDatasetPermissions(
							VitalsSummary,
							DatasetPermissions.VitalsSummary
						)}
					/>
					<Route
						exact={true}
						path="/organizations/:organizationId/datasets/admin"
						component={requiresPermissions(
							DatasetListView,
							Permissions.GlobalAdmins
						)}
					/>
					<Route
						exact={true}
						path="/organizations/:organizationId/datasets"
						component={MultiOrgDatasetListView}
					/>
					<Route
						exact={true}
						path="/organizations/:organizationId/datasets/:datasetId/clients"
						component={requiresDatasetPermissions(
							ClientsListWithUser,
							DatasetPermissions.ClientsListView
						)}
					/>
					<Route
						exact={true}
						path="/organizations/:organizationId/datasets/:datasetId/results"
						component={DatasetAssessmentResultsWithUser}
					/>
					<Route
						exact={true}
						path="/organizations/:organizationId/datasets/:datasetId/results/:resultId"
						component={DatasetReportTemplateContainer}
					/>
					<Route
						path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/sessionId/:sessionId/core/:coreId"
						render={(props) => (
							<CoreTabs {...props} disabled>
								<Route
									exact={true}
									path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/sessionId/:sessionId/core/:coreId/general-appraisal"
									component={requiresDatasetPermissions(
										CoreGeneral,
										DatasetPermissions.CoreGeneral
									)}
								/>
								<Route
									exact={true}
									path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/sessionId/:sessionId/core/:coreId/skills-appraisal"
									component={requiresDatasetPermissions(
										CoreSkillAppraisal,
										DatasetPermissions.CoreSkillAppraisal
									)}
								/>
								<Route
									exact={true}
									path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/sessionId/:sessionId/core/:coreId/excelerate"
									component={requiresDatasetPermissions(
										CoreErrorEntryWithApi,
										DatasetPermissions.CoreErrorEntry
									)}
								/>
							</CoreTabs>
						)}
					/>
					<Route
						path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId"
						render={(props) => (
							<ClientTab {...props}>
								<Route
									exact={true}
									path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/details"
									component={requiresDatasetPermissions(
										ClientDetails,
										DatasetPermissions.ClientDetails
									)}
								/>
								<Route
									exact={true}
									path="/organizations/:organizationId/datasets/:datasetId/clients/:clientId/results"
									component={ClientResultsWithUser} // this page will need more permission(maybe?)
								/>
							</ClientTab>
						)}
					/>

					<Route
						exact={true}
						path={
							"/organizations/:organizationId/datasets/:datasetId/sessionid/:sessionId/vitalsm/:careTestResultId/productid/:productId/summary"
						}
						component={VitalsMSummary}
					/>

					<Route
						exact={true}
						path={"/client-training"}
						component={ClientTrainingPage}
					/>
					<Route
						path={"/client-training/course/:courseId"}
						component={ClientCoursePage}
					/>

					<Route path="/reset-password" component={ResetPasswordPage} />

					<Route exact={true} path="/" component={LandingPage} />

					<Route path="*" component={FourOhFourPage} />
				</Switch>
				<PageFooter />
			</ScrollToTop>
		);
	}
}

export default Routes;
